import React, { useState, useEffect, useRef } from "react";
import { Button, Col, Row, Container, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";

import {
    updateStudy,
    selectStudy,
    selectStudySetupInfo,
    getStudy,
    getStudySetupInfo,
    addParticipants
} from "../../redux/reducers/studySlice";

import HtmlEditor from "./procedure_editors/HtmlEditor";
import { getGroupsApi } from "../../api/studyAlignApi";

import LoadingScreen from "../../components/LoadingScreen";
import StudyCreationLayout, { CreationSteps, StudyStatus, isStudyOver } from "./navigation_logic/StudyCreationLayout";

export default function EditInformation(props) {

    const { status } = props

    const { study_id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [title, setTitle] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [amountParticipants, setAmountParticipants] = useState('')
    const [description, setDescription] = useState('')
    const [consent, setConsent] = useState('')
    // Which field ('description' | 'consent') is awaiting overwrite confirmation.
    const [confirmExample, setConfirmExample] = useState(null)
    const [privateStudy, setPrivateStudy] = useState(false)
    const [groupId, setGroupId] = useState('')
    const [groups, setGroups] = useState([])

    const endDateRef = useRef('')
    const amountParticipantsRef = useRef('')

    const study = useSelector(selectStudy)
    const studySetupInfo = useSelector(selectStudySetupInfo)

    useEffect(() => {
        dispatch(getStudy(study_id))
        dispatch(getStudySetupInfo(study_id))
        getGroupsApi().then((r) => setGroups(r.body || [])).catch(() => {})
    }, [])

    useEffect(() => {
        if (study && studySetupInfo) {
            setTitle(study.name)
            setStartDate(study.startDate.split('T')[0])
            setEndDate(study.endDate.split('T')[0])
            endDateRef.current = study.endDate.split('T')[0]
            let amount;
            if (studySetupInfo.state === "setup") {
                amount = studySetupInfo.planned_number_participants
            } else {
                amount = study.total_participants
            }
            setAmountParticipants(amount)
            amountParticipantsRef.current = amount
            setDescription(study.description)
            setConsent(study.consent)
            setPrivateStudy(study.invite_only)
            setGroupId(study.group_id ? String(study.group_id) : '')
        }
    }, [study, studySetupInfo])

    if (study == null || studySetupInfo == null) {
        return <LoadingScreen />
    }

    // Lock a launched (Active) study only while it is still live; once it is over
    // it can be edited again (the start/end dates and participant count fields).
    const disabled = status === StudyStatus.Active && !isStudyOver(study)

    const handleTitle = (event) => {
        setTitle(event.target.value)
    }

    const handleStartDate = (event) => {
        setStartDate(event.target.value)
    }

    const handleEndDate = (event) => {
        setEndDate(event.target.value)
    }

    const handleParticipants = (event) => {
        setAmountParticipants(event.target.value)
    }

    const handlePrivateStudy = (event) => {
        setPrivateStudy(event.target.checked)
    }

    const handleDescription = (_, value) => {
        setDescription(value)
    }

    const handleConsent = (_, value) => {
        setConsent(value)
    }

    // Load an example HTML outline (from public/examples) into a field.
    // True when a field holds real content (not empty / an empty Quill paragraph).
    const hasContent = (html) =>
        !!html && html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length > 0

    // Insert requested: overwrite straight away when the field is empty, else ask
    // for confirmation first (inline, see the prompt next to the label).
    const insertExample = (field) => {
        const current = field === 'description' ? description : consent
        if (hasContent(current)) {
            setConfirmExample(field)
        } else {
            applyExample(field)
        }
    }

    const applyExample = (field) => {
        const file = field === 'description' ? 'study-description.html' : 'study-consent.html'
        fetch(process.env.PUBLIC_URL + '/examples/' + file)
            .then((r) => r.text())
            .then((html) => {
                if (field === 'description') setDescription(html)
                else setConsent(html)
                setConfirmExample(null)
            })
            .catch(() => {})
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (disabled && amountParticipants > amountParticipantsRef.current) {
            await dispatch(addParticipants({
                "studyId": study_id,
                "amount": amountParticipants - amountParticipantsRef.current
            }))
        }
        await dispatch(updateStudy({
            "studyId": study_id,
            "study": {
                "name": title,
                "startDate": startDate + "T15:08:50.161Z",
                "endDate": endDate + "T15:08:50.161Z",
                "invite_only": privateStudy,
                "description": description,
                "consent": consent,
                "planned_number_participants": amountParticipants,
                "current_setup_step": "information",
                "group_id": groupId ? parseInt(groupId, 10) : null
            }
        }))
        await dispatch(getStudySetupInfo(study_id))
        if (studySetupInfo.state === "setup") {
            navigate("/create/" + study_id + "/procedure")
        } else {
            navigate("/edit/" + study_id + "/procedure")
        }
    }

    return(
        <StudyCreationLayout step={CreationSteps.Information}>

            <Container>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Form.Group className="mb-3" controlId="formTitle">
                            <Form.Label>Title</Form.Label>
                            <Form.Control required type="text" placeholder="Title" value={title} onChange={handleTitle}/>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Col>
                            <Form.Group className="mb-3" controlId="formStartDate">
                                <Form.Label>Start Date</Form.Label>
                                <Form.Control disabled={disabled} required type="date" placeholder="Start Date" value={startDate} onChange={handleStartDate}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()} style={{ cursor: disabled ? "not-allowed" : "pointer" }} />
                            </Form.Group>
                        </Col>
                        <Col>
                            <Form.Group className="mb-3" controlId="formEndDate">
                                <Form.Label>End Date</Form.Label>
                                <Form.Control min={disabled ? (new Date(endDateRef.current) > new Date() ? (new Date()).toISOString().split('T')[0] : endDateRef.current) : startDate}
                                    required type="date" placeholder="End Date" value={endDate} onChange={handleEndDate}
                                    onClick={(e) => e.target.showPicker && e.target.showPicker()} style={{ cursor: "pointer" }} />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Form.Group as={Row} className="mb-3 align-items-center" controlId="formPrivateStudy">
                            <Form.Label column sm={4} xl={3} style={{ whiteSpace: "nowrap" }}>Private Study</Form.Label>
                            <Col sm={8} xl={9}>
                                <Form.Check
                                    type="checkbox"
                                    name="is_active"
                                    checked={privateStudy}
                                    onChange={handlePrivateStudy}
                                    className="me-2"
                                    style={{ cursor: "pointer" }}
                                />
                            </Col>
                        </Form.Group>
                    </Row>

                    <Row>
                        {/* <Col xs="auto">
                            <Form.Group className="mb-3" controlId="formPrivateStudy">
                                <Form.Label>Private Study</Form.Label>
                                <Form.Check type="checkbox" checked={privateStudy} onChange={handlePrivateStudy} />
                            </Form.Group>
                        </Col> */}
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="formParticipants">
                                <Form.Label>Number of Participants</Form.Label>
                                <Form.Control min={disabled ? amountParticipantsRef.current : 0} required type="number" placeholder="Number of Participants" value={amountParticipants} onChange={handleParticipants}/>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3" controlId="formGroup">
                                <Form.Label>Group</Form.Label>
                                <Form.Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                                    <option value="">No group</option>
                                    {groups.map((g) => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Assign this study to one of your groups to brand it with the group.
                                </Form.Text>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Form.Group className="mb-3" controlId="formDescription">
                            <Form.Label>Description
                                <Button type="button" variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={() => insertExample('description')}>
                                    Insert example
                                </Button>
                            </Form.Label>
                            {confirmExample === 'description' &&
                                <div className="small d-flex flex-wrap align-items-center gap-2 mb-2" style={{ color: '#9a6b2f' }}>
                                    <span>Inserting the example will overwrite your current description.</span>
                                    <Button type="button" variant="link" size="sm" className="p-0" onClick={() => applyExample('description')}>Overwrite</Button>
                                    <span className="text-muted">·</span>
                                    <Button type="button" variant="link" size="sm" className="p-0 text-secondary" onClick={() => setConfirmExample(null)}>Keep current</Button>
                                </div>
                            }
                            <HtmlEditor value={description} onChange={(value) => handleDescription('description', value)}/>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Form.Group className="mb-3" controlId="formConsent">
                            <Form.Label>Consent
                                <Button type="button" variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={() => insertExample('consent')}>
                                    Insert example
                                </Button>
                            </Form.Label>
                            {confirmExample === 'consent' &&
                                <div className="small d-flex flex-wrap align-items-center gap-2 mb-2" style={{ color: '#9a6b2f' }}>
                                    <span>Inserting the example will overwrite your current consent.</span>
                                    <Button type="button" variant="link" size="sm" className="p-0" onClick={() => applyExample('consent')}>Overwrite</Button>
                                    <span className="text-muted">·</span>
                                    <Button type="button" variant="link" size="sm" className="p-0 text-secondary" onClick={() => setConfirmExample(null)}>Keep current</Button>
                                </div>
                            }
                            <HtmlEditor value={consent} onChange={(value) => handleConsent('consent', value)}/>
                        </Form.Group>
                    </Row>

                    <Row>
                        <Col> <Button type="submit">Update and Continue</Button> </Col>
                    </Row>
                </Form>
            </Container>

        </StudyCreationLayout>
    )
}